import UserInterfacePrint from '@mds/icons/Printer';
import UserInterfaceSocialShare from '@mds/icons/ShareNetwork';
import UserInterfaceMail from '@mds/icons/Envelope';
import UserInterfaceTwitter from '@mds/icons/TwitterLogo';
import UserInterfaceFacebook from '@mds/icons/FacebookLogo';
import UserInterfaceLinkedin from '@mds/icons/LinkedinLogo';
import type { SxProps, Theme as ExtendedTheme } from '@mui/material/styles';
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
} from '@mui/material';
import { AlertBox } from 'components/ui/Feedback';
import React, { useRef, useState } from 'react';

import {
    FacebookShareButton,
    LinkedinShareButton,
    TwitterShareButton,
    EmailShareButton,
} from 'react-share';
import { CampaignType } from '../../shared/types';
import { useSocialShare } from '../hooks/use-social-share';

interface ShareArticleDialogProps {
    handleClose: () => void;
    articleId: string;
    articleTitle: string;
}

const actionButtonClassName = 'share-article-action-button';
const actionContentClassName = 'share-article-action-content';
const dialogSocialButtonClassName = 'share-dialog-social-button';
const dialogSocialIconClassName = 'share-dialog-social-icon';
const dialogEmailIconClassName = 'share-dialog-social-icon--email';

const shareActionsRootSx: SxProps<ExtendedTheme> = {
    display: 'flex',
    flexDirection: 'column',
    '@media print': {
        display: 'none',
    },
};

const shareActionsSx: SxProps<ExtendedTheme> = {
    display: 'flex',
    gap: 2,
    mb: 4,
    color: 'text.secondary',
    [`& .${actionButtonClassName}`]: {
        p: 0,
        height: 32,
        width: 32,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        outline: 'none',
        border: 0,
        backgroundColor: 'transparent',
        cursor: 'pointer',
        color: 'text.secondary',
        '&:hover': {
            color: 'text.primary',
        },
    },
    [`& .${actionContentClassName}`]: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'inherit',
    },
};

const articleActionIconButtonSx: SxProps<ExtendedTheme> = {
    color: 'text.secondary',
    '&:hover': {
        color: 'text.primary',
    },
};

const ShareArticleActions = (props: {
    articleId: string;
    articleTitle: string;
}) => {
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const linkedInShare = useSocialShare(
        CampaignType.LinkedIn,
        props.articleId
    );
    const twitterShare = useSocialShare(CampaignType.Twitter, props.articleId);
    const facebookShare = useSocialShare(
        CampaignType.Facebook,
        props.articleId
    );
    const emailShare = useSocialShare(CampaignType.Email, props.articleId);

    const print = () => {
        window.print();
    };

    const openDialog = () => {
        setIsDialogOpen(true);
    };

    const closeDialog = () => {
        setIsDialogOpen(false);
    };

    return (
        <>
            <Box sx={shareActionsRootSx}>
                <Box sx={shareActionsSx}>
                    <IconButton
                        id="print-article"
                        onClick={print}
                        size="small"
                        sx={articleActionIconButtonSx}
                    >
                        <UserInterfacePrint />
                    </IconButton>
                    <IconButton
                        id="share-article-button"
                        onClick={openDialog}
                        size="small"
                        sx={articleActionIconButtonSx}
                    >
                        <UserInterfaceSocialShare />
                    </IconButton>
                    <EmailShareButton
                        url={emailShare.url}
                        subject={props.articleTitle}
                        id="share-article-via-email-button"
                        className={actionButtonClassName}
                    >
                        <span
                            className={actionContentClassName}
                            {...emailShare}
                        >
                            <UserInterfaceMail />
                        </span>
                    </EmailShareButton>
                    <TwitterShareButton
                        url={twitterShare.url}
                        id="share-article-button-twitter"
                        className={actionButtonClassName}
                    >
                        <span
                            className={actionContentClassName}
                            {...twitterShare}
                        >
                            <UserInterfaceTwitter />
                        </span>
                    </TwitterShareButton>
                    <FacebookShareButton
                        url={facebookShare.url}
                        id="share-article-button-facebook"
                        className={actionButtonClassName}
                    >
                        <span
                            className={actionContentClassName}
                            {...facebookShare}
                        >
                            <UserInterfaceFacebook />
                        </span>
                    </FacebookShareButton>
                    <LinkedinShareButton
                        url={linkedInShare.url}
                        id="share-article-button-linked-in"
                        className={actionButtonClassName}
                    >
                        <span
                            className={actionContentClassName}
                            {...linkedInShare}
                        >
                            <UserInterfaceLinkedin />
                        </span>
                    </LinkedinShareButton>
                </Box>
            </Box>
            {isDialogOpen && (
                <ShareArticleDialog
                    handleClose={closeDialog}
                    articleId={props.articleId}
                    articleTitle={props.articleTitle}
                />
            )}
        </>
    );
};

const shareDialogSx: SxProps<ExtendedTheme> = {
    '& .MuiDialog-paper': {
        width: 520,
        maxWidth: 'calc(100% - 32px)',
    },
};

const shareDialogRootSx: SxProps<ExtendedTheme> = (theme) => ({
    maxWidth: 520,
    [theme.breakpoints.down('sm')]: {
        mb: 6,
    },
});

const shareDialogLinkCardSx: SxProps<ExtendedTheme> = (theme) => ({
    bgcolor: 'background.paper',
    p: 2,
    '&:not(:first-of-type)': {
        mt: 2,
        [theme.breakpoints.down('sm')]: {
            pb: 0,
        },
    },
});

const shareDialogLinkWrapperSx: SxProps<ExtendedTheme> = {
    border: 1,
    borderColor: 'divider',
    p: 1,
    cursor: 'text',
    my: 2,
    color: 'text.primary',
};

const shareDialogLinkTextSx: SxProps<ExtendedTheme> = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
};

const shareDialogSocialActionsSx: SxProps<ExtendedTheme> = (theme) => ({
    display: 'flex',
    mt: 2,
    gap: 2.625,
    [theme.breakpoints.up('sm')]: {
        gap: 4.3125,
    },
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'flex-start',
        mt: 1,
        gap: 1,
    },
    [`& .${dialogSocialButtonClassName}`]: {
        height: 72,
        minWidth: 48,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        p: 0,
        outline: 'none',
        border: 0,
        background: 'transparent',
        cursor: 'pointer',
        flexDirection: 'column',
        color: 'text.primary',
        [theme.breakpoints.down('sm')]: {
            height: 'auto',
            flexDirection: 'row',
        },
    },
    [`& .${dialogSocialButtonClassName} svg`]: {
        fill: 'currentColor',
    },
    [`& .${dialogSocialIconClassName}`]: {
        height: 48,
        minHeight: 48,
        maxHeight: 48,
        width: 48,
        minWidth: 48,
        maxWidth: 48,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        mb: 1,
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        '&:hover': {
            bgcolor: 'primary.dark',
        },
        [theme.breakpoints.down('sm')]: {
            mr: 1,
            mb: 0,
        },
    },
    [`& .${dialogSocialIconClassName}.${dialogEmailIconClassName}`]: {
        bgcolor: 'warning.main',
        '&:hover': {
            bgcolor: 'warning.dark',
        },
    },
});

const hiddenInputSx: SxProps<ExtendedTheme> = {
    position: 'fixed',
    top: -10000,
};

const ShareArticleDialog = (props: ShareArticleDialogProps) => {
    const { handleClose } = props;
    const [isLinkAlertShown, setIsLinkAlertShown] = useState<boolean>(false);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const linkedInShare = useSocialShare(
        CampaignType.LinkedIn,
        props.articleId
    );
    const twitterShare = useSocialShare(CampaignType.Twitter, props.articleId);
    const facebookShare = useSocialShare(
        CampaignType.Facebook,
        props.articleId
    );
    const directLinkShare = useSocialShare(
        CampaignType.Link,
        props.articleId,
        window.location.href
    );
    const emailShare = useSocialShare(CampaignType.Email, props.articleId);

    const showLinkAlert = () => {
        setIsLinkAlertShown(true);
    };

    const hideLinkAlert = () => {
        setIsLinkAlertShown(false);
    };

    const copyToClipboard = () => {
        if (!inputRef.current) {
            return;
        }
        inputRef.current.select();
        inputRef.current.setSelectionRange(0, 99999);
        document.execCommand('copy');
        showLinkAlert();
    };

    return (
        <Dialog
            id="share-article-dialog"
            sx={shareDialogSx}
            open={true}
            onClose={handleClose}
        >
            <DialogTitle>Share article</DialogTitle>
            <DialogContent>
                <Box sx={shareDialogRootSx}>
                    <AlertBox
                        id="link-copied-to-clipboard-alert"
                        onClose={hideLinkAlert}
                        visible={isLinkAlertShown}
                        styleName="success"
                    >
                        Link copied to clipboard
                    </AlertBox>
                    <Box sx={shareDialogLinkCardSx}>
                        <Typography variant="subtitle1">
                            Copy a link to share
                        </Typography>
                        <Box sx={shareDialogLinkWrapperSx}>
                            <Typography
                                variant="body1"
                                className="share-link"
                                sx={shareDialogLinkTextSx}
                            >
                                {directLinkShare.url}
                            </Typography>
                            <Box
                                component="input"
                                value={directLinkShare.url}
                                readOnly
                                ref={inputRef}
                                type="text"
                                sx={hiddenInputSx}
                            />
                        </Box>
                        <Box
                            onClick={copyToClipboard}
                            ref={directLinkShare.ref}
                        >
                            <Button
                                id="copy-link-button"
                                onClick={directLinkShare.onClick}
                                variant="contained"
                            >
                                Copy link
                            </Button>
                        </Box>
                    </Box>
                    <Box sx={shareDialogLinkCardSx}>
                        <Typography variant="subtitle1">
                            Share it on Social Media
                        </Typography>
                        <Box sx={shareDialogSocialActionsSx}>
                            <EmailShareButton
                                url={emailShare.url}
                                subject={props.articleTitle}
                                id="share-button-email"
                                className={dialogSocialButtonClassName}
                            >
                                <span
                                    className={`${dialogSocialIconClassName} ${dialogEmailIconClassName}`}
                                    {...emailShare}
                                >
                                    <UserInterfaceMail />
                                </span>
                                <Typography variant="caption">Email</Typography>
                            </EmailShareButton>
                            <TwitterShareButton
                                url={twitterShare.url}
                                id="share-button-twitter"
                                className={dialogSocialButtonClassName}
                            >
                                <span
                                    className={dialogSocialIconClassName}
                                    {...twitterShare}
                                >
                                    <UserInterfaceTwitter />
                                </span>
                                <Typography variant="caption">
                                    Twitter
                                </Typography>
                            </TwitterShareButton>
                            <FacebookShareButton
                                url={facebookShare.url}
                                id="share-button-facebook"
                                className={dialogSocialButtonClassName}
                            >
                                <span
                                    className={dialogSocialIconClassName}
                                    {...facebookShare}
                                >
                                    <UserInterfaceFacebook />
                                </span>
                                <Typography variant="caption">
                                    Facebook
                                </Typography>
                            </FacebookShareButton>
                            <LinkedinShareButton
                                url={linkedInShare.url}
                                id="share-button-linked-in"
                                className={dialogSocialButtonClassName}
                            >
                                <span
                                    className={dialogSocialIconClassName}
                                    {...linkedInShare}
                                >
                                    <UserInterfaceLinkedin />
                                </span>
                                <Typography variant="caption">
                                    LinkedIn
                                </Typography>
                            </LinkedinShareButton>
                        </Box>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default ShareArticleActions;
